import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from './api';
import { toast } from 'sonner';
import { Clock, LogIn, LogOut, Calendar, Search, Users, AlertTriangle, CheckCircle, XCircle, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export default function Attendance() {
  const [attendance, setAttendance] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().split('T')[0]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  const user = JSON.parse(localStorage.getItem('userData') || localStorage.getItem('hrms_user') || '{}');
  const isHRAdmin = ['admin', 'hr_manager'].includes(user.role);
  const isManager = ['manager', 'department_head'].includes(user.role);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Managers fetch their team, others fetch all
      const employeeEndpoint = isManager 
        ? `${API}/my-team` 
        : `${API}/employees`;
      
      const [attendanceRes, employeesRes] = await Promise.all([
        axios.get(`${API}/attendance`),
        axios.get(employeeEndpoint)
      ]);
      setAttendance(attendanceRes.data);
      setEmployees(employeesRes.data);
      
      // For managers, default to their own employee_id for clock in/out
      if (isManager && user.employee_id) {
        setSelectedEmployee(user.employee_id);
      } else if (employeesRes.data.length > 0) {
        setSelectedEmployee(employeesRes.data[0].employee_id);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleClockIn = async () => {
    // Managers can only clock in for themselves
    const clockEmployee = isManager ? user.employee_id : selectedEmployee;
    
    if (!clockEmployee) {
      toast.error('Please select an employee');
      return;
    }
    try {
      const response = await axios.post(`${API}/attendance/clock-in?employee_id=${clockEmployee}`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock in');
    }
  };

  const handleClockOut = async () => {
    // Managers can only clock out for themselves
    const clockEmployee = isManager ? user.employee_id : selectedEmployee;
    
    if (!clockEmployee) {
      toast.error('Please select an employee');
      return;
    }
    try {
      const response = await axios.post(`${API}/attendance/clock-out?employee_id=${clockEmployee}`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock out');
    }
  };

  // Filter attendance by date
  const dateFilteredAttendance = useMemo(() => {
    return attendance.filter(record => {
      const recordDate = new Date(record.date).toISOString().split('T')[0];
      return recordDate === dateFilter;
    });
  }, [attendance, dateFilter]);

  // Calculate attendance statistics
  const attendanceStats = useMemo(() => {
    const stats = {
      total: employees.length,
      present: 0,
      late: 0,
      early: 0,
      absent: 0,
      notClocked: 0
    };
    
    const clockedInEmployees = new Set(dateFilteredAttendance.map(a => a.employee_id));
    const workStartTime = new Date(`${dateFilter}T09:00:00`);
    const earlyTime = new Date(`${dateFilter}T08:30:00`);
    
    dateFilteredAttendance.forEach(record => {
      if (record.clock_in) {
        const clockInTime = new Date(record.clock_in);
        if (clockInTime <= earlyTime) {
          stats.early++;
        } else if (clockInTime > workStartTime) {
          stats.late++;
        } else {
          stats.present++;
        }
      }
    });
    
    // Count employees who haven't clocked in
    employees.forEach(emp => {
      if (!clockedInEmployees.has(emp.employee_id)) {
        stats.notClocked++;
      }
    });
    
    stats.absent = stats.notClocked;
    
    return stats;
  }, [dateFilteredAttendance, employees, dateFilter]);

  // Categorize each attendance record
  const getAttendanceStatus = (record) => {
    if (!record.clock_in) return 'absent';
    
    const clockInTime = new Date(record.clock_in);
    const workStartTime = new Date(`${dateFilter}T09:00:00`);
    const earlyTime = new Date(`${dateFilter}T08:30:00`);
    
    if (clockInTime <= earlyTime) return 'early';
    if (clockInTime > workStartTime) return 'late';
    return 'on_time';
  };

  // Filter attendance records
  const filteredAttendance = useMemo(() => {
    let filtered = dateFilteredAttendance;
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(record => getAttendanceStatus(record) === statusFilter);
    }
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(record => {
        const employee = employees.find(e => e.employee_id === record.employee_id);
        return (
          employee?.full_name?.toLowerCase().includes(query) ||
          record.employee_id?.toLowerCase().includes(query) ||
          employee?.email?.toLowerCase().includes(query)
        );
      });
    }
    
    return filtered;
  }, [dateFilteredAttendance, statusFilter, searchQuery, employees]);

  // Get employees who haven't clocked in
  const absentEmployees = useMemo(() => {
    const clockedInEmployees = new Set(dateFilteredAttendance.map(a => a.employee_id));
    return employees.filter(emp => !clockedInEmployees.has(emp.employee_id));
  }, [dateFilteredAttendance, employees]);

  const getStatusBadge = (status) => {
    const styles = {
      early: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', label: 'Early' },
      on_time: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300', label: 'On Time' },
      late: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', label: 'Late' },
      absent: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', label: 'Absent' },
      present: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300', label: 'Present' }
    };
    return styles[status] || styles.present;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 dark:border-slate-100"></div>
      </div>
    );
  }

  // HR Admin View - Analytics and Monitoring
  if (isHRAdmin) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Attendance Overview</h2>
            <p className="text-slate-600 dark:text-slate-400 mt-1">Monitor employee attendance and punctuality</p>
          </div>
        </div>

        {/* Date Selector */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center space-x-4">
            <Calendar className="h-5 w-5 text-slate-600 dark:text-slate-400" />
            <label className="text-sm font-semibold text-slate-900 dark:text-slate-100">Select Date:</label>
            <Input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-auto dark:bg-slate-900 dark:border-slate-600"
            />
            <span className="text-sm text-slate-600 dark:text-slate-400">
              {new Date(dateFilter).toLocaleDateString('en-MY', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
            </span>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => setStatusFilter('all')}
            className={`p-4 rounded-xl border-2 transition-all text-left ${
              statusFilter === 'all' 
                ? 'border-slate-500 bg-slate-50 dark:bg-slate-700' 
                : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-300'
            }`}
          >
            <div className="flex items-center space-x-2 mb-2">
              <Users className="h-5 w-5 text-slate-600 dark:text-slate-400" />
              <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Total Staff</span>
            </div>
            <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">{attendanceStats.total}</p>
          </button>

          <button
            onClick={() => setStatusFilter('early')}
            className={`p-4 rounded-xl border-2 transition-all text-left ${
              statusFilter === 'early' 
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-blue-300'
            }`}
          >
            <div className="flex items-center space-x-2 mb-2">
              <TrendingUp className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-600">Early Birds</span>
            </div>
            <p className="text-3xl font-bold text-blue-600">{attendanceStats.early}</p>
            <p className="text-xs text-slate-500 mt-1">Before 8:30 AM</p>
          </button>

          <button
            onClick={() => setStatusFilter('late')}
            className={`p-4 rounded-xl border-2 transition-all text-left ${
              statusFilter === 'late' 
                ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20' 
                : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-amber-300'
            }`}
          >
            <div className="flex items-center space-x-2 mb-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <span className="text-sm font-medium text-amber-600">Late Arrivals</span>
            </div>
            <p className="text-3xl font-bold text-amber-600">{attendanceStats.late}</p>
            <p className="text-xs text-slate-500 mt-1">After 9:00 AM</p>
          </button>

          <button
            onClick={() => setStatusFilter('absent')}
            className={`p-4 rounded-xl border-2 transition-all text-left ${
              statusFilter === 'absent' 
                ? 'border-red-500 bg-red-50 dark:bg-red-900/20' 
                : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-red-300'
            }`}
          >
            <div className="flex items-center space-x-2 mb-2">
              <XCircle className="h-5 w-5 text-red-600" />
              <span className="text-sm font-medium text-red-600">Not Clocked In</span>
            </div>
            <p className="text-3xl font-bold text-red-600">{attendanceStats.notClocked}</p>
            <p className="text-xs text-slate-500 mt-1">No record</p>
          </button>
        </div>

        {/* Search and Filter */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center space-x-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                placeholder="Search by name, employee ID, or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 dark:bg-slate-900 dark:border-slate-600"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-10 rounded-lg border border-slate-300 dark:border-slate-600 px-4 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
            >
              <option value="all">All Status</option>
              <option value="early">Early Birds</option>
              <option value="on_time">On Time</option>
              <option value="late">Late Arrivals</option>
            </select>
          </div>
        </div>

        {/* Attendance Records */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="p-4 border-b border-slate-200 dark:border-slate-700">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {statusFilter === 'all' ? 'All Attendance Records' : 
               statusFilter === 'early' ? 'Early Arrivals' :
               statusFilter === 'late' ? 'Late Arrivals' :
               statusFilter === 'absent' ? 'Not Clocked In' : 'Attendance Records'}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {statusFilter === 'absent' ? absentEmployees.length : filteredAttendance.length} records found
            </p>
          </div>

          {/* Show absent employees if filter is "absent" */}
          {statusFilter === 'absent' ? (
            <div className="overflow-x-auto">
              {absentEmployees.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="h-12 w-12 text-emerald-300 mx-auto mb-3" />
                  <p className="text-slate-500 dark:text-slate-400">All employees have clocked in today!</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Employee</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Department</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Position</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                    {absentEmployees
                      .filter(emp => {
                        if (!searchQuery) return true;
                        const query = searchQuery.toLowerCase();
                        return emp.full_name?.toLowerCase().includes(query) ||
                               emp.employee_id?.toLowerCase().includes(query) ||
                               emp.email?.toLowerCase().includes(query);
                      })
                      .map((emp) => (
                        <tr key={emp.employee_id} className="hover:bg-slate-50 dark:hover:bg-slate-700">
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-3">
                              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-red-400 to-rose-500 flex items-center justify-center">
                                <span className="text-white font-semibold text-sm">
                                  {emp.full_name?.charAt(0) || 'U'}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium text-slate-900 dark:text-slate-100">{emp.full_name || 'Unknown'}</p>
                                <p className="text-xs text-slate-500 dark:text-slate-400">{emp.employee_id}</p>
                                <p className="text-xs text-slate-400">{emp.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-700 dark:text-slate-300">
                            {emp.department || 'N/A'}
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-700 dark:text-slate-300">
                            {emp.position || 'N/A'}
                          </td>
                          <td className="px-6 py-4">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                              Not Clocked In
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              {filteredAttendance.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-500 dark:text-slate-400">
                    {searchQuery ? 'No employees found matching your search' : 'No attendance records found for this filter'}
                  </p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Employee</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Clock In</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Clock Out</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Working Hours</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                    {filteredAttendance.map((record) => {
                      const employee = employees.find(e => e.employee_id === record.employee_id);
                      const status = getAttendanceStatus(record);
                      const statusStyle = getStatusBadge(status);
                      
                      // Calculate working hours
                      let workingHours = '-';
                      if (record.clock_in && record.clock_out) {
                        const clockIn = new Date(record.clock_in);
                        const clockOut = new Date(record.clock_out);
                        const diffMs = clockOut - clockIn;
                        const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
                        const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                        workingHours = `${diffHrs}h ${diffMins}m`;
                      }
                      
                      return (
                        <tr 
                          key={record.attendance_id} 
                          className="hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-3">
                              <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                                status === 'early' ? 'bg-gradient-to-br from-blue-400 to-indigo-500' :
                                status === 'late' ? 'bg-gradient-to-br from-amber-400 to-orange-500' :
                                'bg-gradient-to-br from-emerald-400 to-teal-500'
                              }`}>
                                <span className="text-white font-semibold text-sm">
                                  {employee?.full_name?.charAt(0) || 'U'}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium text-slate-900 dark:text-slate-100">{employee?.full_name || 'Unknown'}</p>
                                <p className="text-xs text-slate-500 dark:text-slate-400">{record.employee_id}</p>
                                <p className="text-xs text-slate-400">{employee?.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              <LogIn className="h-4 w-4 text-emerald-500" />
                              <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                                {record.clock_in ? new Date(record.clock_in).toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit' }) : '-'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              <LogOut className="h-4 w-4 text-red-500" />
                              <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                                {record.clock_out ? new Date(record.clock_out).toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit' }) : '-'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                              {workingHours}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                              {statusStyle.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Employee/Manager View (Original)
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Attendance Tracking</h2>
        <p className="text-slate-600 dark:text-slate-400 mt-1">
          {isManager ? `Team attendance records (${employees.length} team members)` : `${attendance.length} total records`}
        </p>
      </div>

      {/* Clock In/Out Section - Only for self if manager */}
      {!isManager && (
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-8 text-white">
          <div className="flex items-center space-x-3 mb-6">
            <Clock className="h-8 w-8" />
            <h3 className="text-2xl font-bold">Clock In / Out</h3>
          </div>
          <div className="flex items-end space-x-4">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-2">Select Employee</label>
              <select
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                data-testid="employee-select"
                className="w-full h-11 rounded-xl border-0 px-4 text-slate-900"
              >
                {employees.map(emp => (
                  <option key={emp.employee_id} value={emp.employee_id}>
                    {emp.full_name} ({emp.employee_id})
                  </option>
                ))}
              </select>
            </div>
            <Button
              onClick={handleClockIn}
              data-testid="clock-in-btn"
              className="h-11 bg-white text-emerald-600 hover:bg-emerald-50"
            >
              <LogIn className="h-4 w-4 mr-2" />
              Clock In
            </Button>
            <Button
              onClick={handleClockOut}
              data-testid="clock-out-btn"
              className="h-11 bg-slate-900 text-white hover:bg-slate-800"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Clock Out
            </Button>
          </div>
        </div>
      )}
      
      {/* Manager Clock In/Out - Only for Self */}
      {isManager && (
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-8 text-white">
          <div className="flex items-center space-x-3 mb-6">
            <Clock className="h-8 w-8" />
            <h3 className="text-2xl font-bold">My Clock In / Out</h3>
          </div>
          <div className="flex items-end space-x-4">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-2">Your Attendance</label>
              <div className="w-full h-11 rounded-xl bg-white px-4 text-slate-900 flex items-center font-medium">
                {user.full_name} ({user.employee_id})
              </div>
            </div>
            <Button
              onClick={handleClockIn}
              data-testid="clock-in-btn"
              className="h-11 bg-white text-emerald-600 hover:bg-emerald-50"
            >
              <LogIn className="h-4 w-4 mr-2" />
              Clock In
            </Button>
            <Button
              onClick={handleClockOut}
              data-testid="clock-out-btn"
              className="h-11 bg-slate-900 text-white hover:bg-slate-800"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Clock Out
            </Button>
          </div>
          <p className="text-emerald-100 text-sm mt-4">
            💡 You can only clock in/out for yourself. View your team's attendance below.
          </p>
        </div>
      )}

      {/* Date Filter for Managers */}
      {isManager && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6">
          <div className="flex items-center space-x-4">
            <Calendar className="h-5 w-5 text-slate-600 dark:text-slate-400" />
            <label className="text-sm font-semibold text-slate-900 dark:text-slate-100">Filter by Date:</label>
            <Input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-auto"
            />
            <span className="text-sm text-slate-600 dark:text-slate-400">
              Showing {dateFilteredAttendance.length} record(s) for {new Date(dateFilter).toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
          </div>
        </div>
      )}

      {/* Attendance Records */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Employee</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Clock In</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Clock Out</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {(isManager ? dateFilteredAttendance : attendance).map((record) => {
                const employee = employees.find(e => e.employee_id === record.employee_id);
                return (
                <tr 
                  key={record.attendance_id} 
                  data-testid={`attendance-${record.attendance_id}`} 
                  className="hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                  onClick={() => {
                    const clockIn = record.clock_in ? new Date(record.clock_in).toLocaleTimeString('en-MY') : 'N/A';
                    const clockOut = record.clock_out ? new Date(record.clock_out).toLocaleTimeString('en-MY') : 'N/A';
                    toast.info(`Attendance: ${employee?.full_name || record.employee_id}\nDate: ${new Date(record.date).toLocaleDateString('en-MY')}\nClock In: ${clockIn}\nClock Out: ${clockOut}\nStatus: ${record.status}`);
                  }}
                  title="Click to view details"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">
                    {new Date(record.date).toLocaleDateString('en-MY', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">
                    <div>
                      <div className="font-medium">{employee?.full_name || 'Unknown'}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 font-mono">{record.employee_id}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">
                    {record.clock_in ? new Date(record.clock_in).toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit' }) : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">
                    {record.clock_out ? new Date(record.clock_out).toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit' }) : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`status-badge status-${record.status === 'present' ? 'approved' : 'pending'}`}>
                      {record.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-400">
                    {record.notes || '-'}
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      </div>

      {(isManager ? dateFilteredAttendance : attendance).length === 0 && (
        <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700">
          <Calendar className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
          <p className="text-slate-500 dark:text-slate-400">
            {isManager ? `No attendance records found for ${new Date(dateFilter).toLocaleDateString('en-MY')}` : 'No attendance records found'}
          </p>
        </div>
      )}
    </div>
  );
}
